import type { GameState } from './game';

export const HINTS_PER_DAY = 2;
export const INSIGHTS_PER_DAY = 3;

/** Day-scoped hint/insight budget (spec §3.6–3.7). Resets at local midnight. */
export interface DailyQuotas {
  dateKey: string;
  hintsLeft: number;
  insightsLeft: number;
}

/** Returns stored quotas if they belong to `dateKey`, else a fresh budget. */
export function quotasFor(dateKey: string, stored: DailyQuotas | null): DailyQuotas {
  if (stored && stored.dateKey === dateKey) return stored;
  return { dateKey, hintsLeft: HINTS_PER_DAY, insightsLeft: INSIGHTS_PER_DAY };
}

/** Positions already solved green by any guess. */
function greenPositions(state: GameState): Set<number> {
  const green = new Set<number>();
  for (const { tiles } of state.guesses) {
    tiles.forEach((t, i) => { if (t === 'correct') green.add(i); });
  }
  return green;
}

/** Leftmost answer position that is neither green nor already revealed; null if none. */
export function hintPosition(state: GameState): number | null {
  const green = greenPositions(state);
  const revealed = new Set(state.revealedHints);
  // The answer's own length is the board width (spec §10.1).
  for (let i = 0; i < state.answer.length; i++) {
    if (!green.has(i) && !revealed.has(i)) return i;
  }
  return null;
}

/** Pure: records a hint reveal. Throws if no position is available. */
export function applyHint(state: GameState): GameState {
  const pos = hintPosition(state);
  if (pos === null) throw new Error('no hint position available');
  return {
    ...state,
    hintsUsed: state.hintsUsed + 1,
    revealedHints: [...state.revealedHints, pos],
  };
}
